import React from 'react';
import { Text, Image, StyleSheet, TouchableOpacity } from 'react-native';

import { Box } from '@components/Restyle';
import theme from '@themes/theme';
import {
  buildSignupWithEmailUrl,
  resolveInvitationLinkParams,
} from '@utils/invitationWebUrl';

const InvitedUserForRegister = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let invitationToken = urlParams.get('invitation_token') ?? '';
  let type = urlParams.get('type') ?? '';
  const id = urlParams.get('id') ?? '';

  let fromInvitationURL = true;
  const path = window.location.pathname;
  // please refer https://tasktag.atlassian.net/browse/TA-5972
  if (path.startsWith('/account_invitations/')) {
    fromInvitationURL = false;
    const code = path.substring('/account_invitations/'.length);
    invitationToken = code;
    type = 'team';
  }

  const isMobileBrowser = (): boolean => {
    if (typeof window === 'undefined') return false;
    const userAgent = window.navigator.userAgent;
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    );
  };

  const getInvitationUrlWeb = () => {
    // Build URL from origin + path so it works even when the address bar was
    // updated to /invited-user but path was captured as /account_invitations/xxx
    const origin = window.location.origin ?? '';
    if (fromInvitationURL) {
      // Token may be in query; if we're on /invited-user, token might be in sessionStorage
      return buildSignupWithEmailUrl(
        origin,
        resolveInvitationLinkParams({ token: invitationToken, type, id })
      );
    }
    return buildSignupWithEmailUrl(origin, {
      token: invitationToken,
      type: 'team',
      id,
    });
  };

  const inviteUrlMobile = type
    ? 'com.eloveit.tasktag://register/' + invitationToken + `/${type}/` + id
    : process.env.ANDROID_DEEP_LINK + invitationToken;

  const appStoreUrl = 'https://onelink.to/uzr863';

  React.useEffect(() => {
    if (isMobileBrowser()) {
      let redirectTimer: number;
      let hasAppOpened = false;

      const detectAppInstallation = () => {
        const startTime = Date.now();

        const handleVisibilityChange = () => {
          if (document.hidden) {
            hasAppOpened = true;
            clearTimeout(redirectTimer);
          }
        };

        const handleBlur = () => {
          hasAppOpened = true;
          clearTimeout(redirectTimer);
        };

        const handleFocus = () => {
          const timeElapsed = Date.now() - startTime;
          if (timeElapsed < 1000 && !hasAppOpened) {
            setTimeout(() => {
              if (!hasAppOpened) {
                if (typeof window !== 'undefined' && window.location?.href) {
                  window.location.href = appStoreUrl;
                }
              }
            }, 1500);
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);

        if (typeof window !== 'undefined' && window.location?.href) {
          window.location.href = inviteUrlMobile ?? '';
        }

        redirectTimer = setTimeout(() => {
          if (!hasAppOpened) {
            window.location.href = appStoreUrl;
          }
        }, 2500);

        return () => {
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange
          );
          window.removeEventListener('blur', handleBlur);
          window.removeEventListener('focus', handleFocus);
          clearTimeout(redirectTimer);
        };
      };

      const cleanup = detectAppInstallation();

      return cleanup;
    }
  }, [inviteUrlMobile, appStoreUrl]);

  if (isMobileBrowser()) {
    return (
      <Box flex={1} justifyContent='center' alignItems='center'>
        <Image
          style={styles.image}
          source={require('../../../assets/appIcon.png')}
        />
        <Box width={400}>
          <Text style={styles.text}>
            Checking if TaskTag app is installed...
          </Text>
          <Text style={styles.text}>
            If the app is installed, it will open automatically. Otherwise,
            you'll be redirected to the app store to download it.
          </Text>
          <TouchableOpacity
            style={styles.button}
            onPress={() => {
              if (typeof window !== 'undefined') {
                window.open(getInvitationUrlWeb(), '_blank');
              }
            }}>
            <Text style={styles.buttonText}>Continue in Web Browser</Text>
          </TouchableOpacity>
        </Box>
      </Box>
    );
  }

  return (
    <Box flex={1} justifyContent='center' alignItems='center'>
      <Image
        style={styles.image}
        source={require('../../../assets/appIcon.png')}
      />

      <Box width={400}>
        <Text style={styles.text}>
          If you already have the task tag application. Please click on this
          button
        </Text>

        <TouchableOpacity
          style={styles.button}
          onPress={() => {
            if (typeof window !== 'undefined') {
              window.open(inviteUrlMobile ?? '', '_blank');
            }
          }}>
          <Text style={styles.buttonText}>Open TaskTag App</Text>
        </TouchableOpacity>

        <Text style={styles.text}>
          If you don't have the task tag application. Please click on this
          button
        </Text>

        <TouchableOpacity
          style={styles.button}
          onPress={() => {
            if (typeof window !== 'undefined') {
              window.open(getInvitationUrlWeb(), '_blank');
            }
          }}>
          <Text style={styles.buttonText}>Open TaskTag Web</Text>
        </TouchableOpacity>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  image: {
    width: 150,
    height: 150,
  },
  text: {
    margin: 30,
    fontSize: 15,
    fontWeight: '500',
    textAlign: 'center',
    marginHorizontal: 20,
  },
  button: {
    padding: 10,
    width: 200,
    borderRadius: 5,
    alignSelf: 'center',
    alignItems: 'center',
    backgroundColor: 'black',
    marginVertical: 10,
  },
  buttonText: {
    color: theme.colors.white,
    fontWeight: '600',
  },
});

export default InvitedUserForRegister;
